import type {
  MoveAction,
  RunHorsesBoard,
  RunHorsesCell,
  RunHorsesMoveKind,
  RunHorsesPlayerId,
  RunHorsesPosition,
  RunHorsesState,
  RunHorsesViewerState,
} from "../types";
import {
  RUN_HORSES_BOARD_SIZE,
  RUN_HORSES_NO_KNIGHT_RADIUS,
  RUN_HORSES_OASIS,
  RUN_HORSES_PLAYERS,
} from "../types";

const ORTHOGONAL_DIRECTIONS = [
  { row: -1, col: 0 },
  { row: 1, col: 0 },
  { row: 0, col: -1 },
  { row: 0, col: 1 },
] as const;

const KNIGHT_DELTAS = [
  { row: -2, col: -1 },
  { row: -2, col: 1 },
  { row: -1, col: -2 },
  { row: -1, col: 2 },
  { row: 1, col: -2 },
  { row: 1, col: 2 },
  { row: 2, col: -1 },
  { row: 2, col: 1 },
] as const;

export function positionKey(position: RunHorsesPosition): string {
  return `${position.row},${position.col}`;
}

export function positionsEqual(
  a: RunHorsesPosition,
  b: RunHorsesPosition,
): boolean {
  return a.row === b.row && a.col === b.col;
}

export function isInBounds(row: number, col: number): boolean {
  return (
    Number.isInteger(row) &&
    Number.isInteger(col) &&
    row >= 0 &&
    row < RUN_HORSES_BOARD_SIZE &&
    col >= 0 &&
    col < RUN_HORSES_BOARD_SIZE
  );
}

export function getCell(
  board: RunHorsesBoard,
  row: number,
  col: number,
): RunHorsesCell | null {
  if (!isInBounds(row, col)) return null;
  return board[row]?.[col] ?? null;
}

export function setCell(
  board: RunHorsesBoard,
  row: number,
  col: number,
  value: RunHorsesCell,
): RunHorsesBoard {
  if (!isInBounds(row, col)) {
    throw new Error("Cannot set a cell outside the Run Horses board");
  }
  return board.map((line, lineIndex) =>
    lineIndex === row
      ? line.map((cell, cellIndex) => (cellIndex === col ? value : cell))
      : line,
  );
}

export function isPlayerId(value: unknown): value is RunHorsesPlayerId {
  return (
    typeof value === "string" &&
    RUN_HORSES_PLAYERS.includes(value as RunHorsesPlayerId)
  );
}

export function getOpponentPlayerId(
  playerId: RunHorsesPlayerId,
): RunHorsesPlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

export function isOasis(row: number, col: number): boolean {
  return row === RUN_HORSES_OASIS.row && col === RUN_HORSES_OASIS.col;
}

export function manhattanToOasis(row: number, col: number): number {
  return (
    Math.abs(row - RUN_HORSES_OASIS.row) + Math.abs(col - RUN_HORSES_OASIS.col)
  );
}

/** Knights cannot land within Manhattan distance 2 of the oasis (incl. oasis). */
export function isNoKnightLandCell(row: number, col: number): boolean {
  return manhattanToOasis(row, col) <= RUN_HORSES_NO_KNIGHT_RADIUS;
}

/** Rook-style slide: travel in one direction until the wall or another piece. */
export function slideDestination(
  board: RunHorsesBoard,
  from: RunHorsesPosition,
  direction: { readonly row: number; readonly col: number },
): RunHorsesPosition {
  let row = from.row;
  let col = from.col;
  while (true) {
    const nextRow = row + direction.row;
    const nextCol = col + direction.col;
    if (!isInBounds(nextRow, nextCol)) break;
    if (getCell(board, nextRow, nextCol) !== "empty") break;
    row = nextRow;
    col = nextCol;
  }
  return { row, col };
}

/** Reachable slide destinations (excludes the no-move case). */
export function getSlideTargets(
  board: RunHorsesBoard,
  from: RunHorsesPosition,
): readonly RunHorsesPosition[] {
  const targets: RunHorsesPosition[] = [];
  for (const direction of ORTHOGONAL_DIRECTIONS) {
    const destination = slideDestination(board, from, direction);
    if (!positionsEqual(destination, from)) {
      targets.push(destination);
    }
  }
  return targets;
}

/** Knight jumps to empty cells outside the no-knight zone. */
export function getKnightTargets(
  board: RunHorsesBoard,
  from: RunHorsesPosition,
): readonly RunHorsesPosition[] {
  const targets: RunHorsesPosition[] = [];
  for (const delta of KNIGHT_DELTAS) {
    const row = from.row + delta.row;
    const col = from.col + delta.col;
    if (!isInBounds(row, col)) continue;
    if (getCell(board, row, col) !== "empty") continue;
    if (isNoKnightLandCell(row, col)) continue;
    targets.push({ row, col });
  }
  return targets;
}

export function listPlayerPieces(
  board: RunHorsesBoard,
  playerId: RunHorsesPlayerId,
): readonly RunHorsesPosition[] {
  const pieces: RunHorsesPosition[] = [];
  for (let row = 0; row < RUN_HORSES_BOARD_SIZE; row += 1) {
    for (let col = 0; col < RUN_HORSES_BOARD_SIZE; col += 1) {
      if (getCell(board, row, col) === playerId) {
        pieces.push({ row, col });
      }
    }
  }
  return pieces;
}

/** All legal moves for one player on a given board (slide + knight). */
export function enumerateMovesOnBoard(
  board: RunHorsesBoard,
  playerId: RunHorsesPlayerId,
): readonly MoveAction[] {
  const moves: MoveAction[] = [];
  for (const from of listPlayerPieces(board, playerId)) {
    for (const to of getSlideTargets(board, from)) {
      moves.push({ type: "Move", playerId, from, to, kind: "slide" });
    }
    for (const to of getKnightTargets(board, from)) {
      moves.push({ type: "Move", playerId, from, to, kind: "knight" });
    }
  }
  return moves;
}

export function enumerateLegalMoves(
  state: RunHorsesState,
  playerId: RunHorsesPlayerId = state.currentPlayerId,
): readonly MoveAction[] {
  if (state.phase !== "playing") return [];
  return enumerateMovesOnBoard(state.board, playerId);
}

/** The player occupying the oasis, if any. */
export function checkWinner(board: RunHorsesBoard): RunHorsesPlayerId | null {
  const cell = getCell(board, RUN_HORSES_OASIS.row, RUN_HORSES_OASIS.col);
  return isPlayerId(cell) ? cell : null;
}

/** Move a piece on the board, returning a new board (no capture). */
export function applyMoveOnBoard(
  board: RunHorsesBoard,
  move: MoveAction,
): RunHorsesBoard {
  const cleared = setCell(board, move.from.row, move.from.col, "empty");
  return setCell(cleared, move.to.row, move.to.col, move.playerId);
}

/** Deterministic move ordering (to, then kind, then from). */
export function compareMove(a: MoveAction, b: MoveAction): number {
  if (a.to.row !== b.to.row) return a.to.row - b.to.row;
  if (a.to.col !== b.to.col) return a.to.col - b.to.col;
  if (a.kind !== b.kind) return moveKindRank(a.kind) - moveKindRank(b.kind);
  if (a.from.row !== b.from.row) return a.from.row - b.from.row;
  return a.from.col - b.from.col;
}

function moveKindRank(kind: RunHorsesMoveKind): number {
  return kind === "slide" ? 0 : 1;
}

/**
 * Simple, deterministic greedy heuristic: take a winning move if one exists,
 * otherwise the legal move whose destination is closest (Manhattan) to the
 * oasis, breaking ties deterministically. Pure — no RNG, no side effects.
 */
export function chooseHeuristicMove(state: RunHorsesState): MoveAction | null {
  const moves = enumerateLegalMoves(state);
  if (moves.length === 0) return null;
  const winning = moves.filter((move) => isOasis(move.to.row, move.to.col));
  const pool = winning.length > 0 ? winning : moves;

  let best = pool[0]!;
  let bestScore = manhattanToOasis(best.to.row, best.to.col);
  for (let index = 1; index < pool.length; index += 1) {
    const move = pool[index]!;
    const score = manhattanToOasis(move.to.row, move.to.col);
    if (
      score < bestScore ||
      (score === bestScore && compareMove(move, best) < 0)
    ) {
      best = move;
      bestScore = score;
    }
  }
  return best;
}

// Complete-information game: viewer state is the full state.
export function filterStateForViewer(
  state: RunHorsesState,
  _viewerPlayerId: string | null,
): RunHorsesViewerState {
  return state;
}

export function buildViewerStates(
  state: RunHorsesState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, RunHorsesViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
